import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    Skeleton,
    Typography,
    Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from 'src/common/components/ConfirmDialog';

import { Tender } from './dto/tender.dto';
import { asGermanDateTime } from 'src/common/date';
import { deleteTender, getListOfTenders } from './offer-evaluation.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from '../notifications/notifications.slice';
import { buildOfferEvaluationUrl } from './hooks';

export default function OfferEvaluationsListPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { errorMessage, tenderList } = useAppSelector((state) => state.offerEvaluation);
    const dispatch = useAppDispatch();

    const [page, setPage] = useState(1);
    const [tenderToDelete, setTenderToDelete] = useState<Tender | null>(null);

    useEffect(() => {
        dispatch(getListOfTenders(page));
    }, [page, dispatch]);

    useEffect(() => {
        if (errorMessage) {
            dispatch(showSnackbar({ message: errorMessage, severity: NotificationSeverity.Error }));
        }
    }, [errorMessage, dispatch]);

    const handleListItemClick = (tender: Tender) => {
        navigate(buildOfferEvaluationUrl(tender.id, 1));
    };

    const handleDeleteClick = (e: React.MouseEvent, tender: Tender) => {
        e.stopPropagation();
        setTenderToDelete(tender);
    };

    const handleDeleteConfirm = async () => {
        if (!tenderToDelete) return;
        await dispatch(deleteTender(tenderToDelete.id));
        setTenderToDelete(null);
    };

    const handleDeleteCancel = () => {
        setTenderToDelete(null);
    };

    if (!tenderList) {
        return <Skeleton variant="rectangular" width="100%" height={800} />;
    }

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Offer Evaluations')}
            </Typography>

            <Table sx={{ minWidth: 650 }} aria-label="offer evaluations table">
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            '& .MuiTableCell-root': { color: 'primary.contrastText' },
                        }}
                    >
                        <TableCell>{t('Project name')}</TableCell>
                        <TableCell>{t('Created at')}</TableCell>
                        <TableCell>{t('Created by')}</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tenderList.content.map((tender) => (
                        <TableRow
                            key={tender.id}
                            hover
                            onClick={() => handleListItemClick(tender)}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell>{tender.projectName}</TableCell>
                            <TableCell>{asGermanDateTime(tender.createdAt)}</TableCell>
                            <TableCell>{tender.createdBy}</TableCell>
                            <TableCell align="right">
                                <Button
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                    aria-label={t('Delete')}
                                    startIcon={<DeleteIcon fontSize="small" />}
                                    onClick={(e) => handleDeleteClick(e, tender)}
                                >
                                    {t('Delete')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Pagination
                variant="outlined"
                count={tenderList.page.totalPages}
                page={page}
                onChange={(_, page) => setPage(page)}
            />

            <ConfirmDialog
                open={!!tenderToDelete}
                title={t('Delete Tender')}
                message={t('Are you sure you want to delete "{{name}}"? This action cannot be undone.', {
                    name: tenderToDelete?.projectName,
                })}
                confirmLabel={t('Delete')}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </>
    );
}
